import {
  type ReactNode,
  type RefObject,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Button } from '../Button/Button';
import { cx } from '../cx';

/**
 * LONG TEXT WITH "MORE" (redesign register L17, R-K1; brief FE-FOUNDATION section 4;
 * components page section 8).
 *
 * The text is clamped to a number of lines - 2 for quotes, reasons, problems and task titles,
 * 3 for intake answers, 4 for the situation (component map part 9) - and "More" appears only
 * when the text really is cut. "More" opens the rest, "Less" folds it again (L17: Arif's
 * label, a recorded exception to the rulebook). Both are text actions, the same blue words as
 * "Mark as handled" (components page section 2).
 *
 * THE FULL TEXT ALWAYS REACHES A SCREEN READER. The clamp hides the overflow visually and
 * removes nothing from the document, so a reader hears every word whether the block is open
 * or not; the button says whether it is open with `aria-expanded`.
 *
 * "MORE" IS A BUTTON, SO IT NEVER SITS INSIDE A LINK. A clamped quote inside a row link (the
 * Dashboard's Waiting rows) takes its expander OUTSIDE the link: the row calls
 * `useExpandableText()`, renders `ExpandableText` with that state in the link and
 * `ExpandableTextToggle` with the same state in the row's actions (`ui/DashboardRow`'s
 * `actions` slot). Without a state passed in, the block draws its own toggle under the text.
 */
export type ExpandableLines = 2 | 3 | 4;

const CLAMP: Readonly<Record<ExpandableLines, string>> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

/**
 * WHAT THE "MORE" TOGGLE NEEDS, AND NOTHING ELSE (BR-PARTS). A clamped text block gets it from
 * `useExpandableText()`; a block of content that folds by its own rule - the record page's
 * fields past the eighth (`app/record/RecordDetails.tsx`, `FieldCells`) - hands in its own
 * open state, `cut: true` and the id of the region it reveals, so the product has one expander
 * with one pair of words (L17 "More", R-W1 "Less") instead of a second control for the same job.
 */
export interface ExpandableToggleState {
  readonly expanded: boolean;
  readonly setExpanded: (next: boolean) => void;
  /** True when there is more to show: for a clamp, only when the text is taller than its box. */
  readonly cut: boolean;
  /** The id of the block or region the toggle opens (`aria-controls`). */
  readonly bodyId: string;
}

export interface ExpandableTextState extends ExpandableToggleState {
  /** @internal The body measures itself through this. */
  readonly bodyRef: RefObject<HTMLDivElement | null>;
  /** @internal Re-measures; the body calls it when its text or width changes. */
  readonly measure: () => void;
}

/** `initiallyExpanded` is for a picture of the open state (the states page); pages start folded. */
export function useExpandableText(initiallyExpanded = false): ExpandableTextState {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [cut, setCut] = useState(initiallyExpanded);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const bodyId = useId();
  const measure = useCallback(() => {
    const element = bodyRef.current;
    if (element === null) return;
    /* Measured only while clamped: an open block is as tall as its text by definition, and
       "Less" must stay while it is open. One pixel of slack absorbs sub-pixel line boxes. */
    if (element.dataset['expanded'] === 'true') return;
    setCut(element.scrollHeight > element.clientHeight + 1);
  }, []);
  return { expanded, setExpanded, cut, bodyId, bodyRef, measure };
}

export interface ExpandableTextProps {
  readonly lines: ExpandableLines;
  readonly children: ReactNode;
  /** The caller's text colour and size; the block adds only the clamp. */
  readonly className?: string | undefined;
  /**
   * A state from `useExpandableText()`, when the toggle has to stand somewhere else (outside
   * a link). Without it the block keeps its own state and draws the toggle under the text.
   */
  readonly state?: ExpandableTextState | undefined;
  /** What the block holds, for its own toggle's accessible name (`ExpandableTextToggle`). */
  readonly subject?: string | undefined;
}

export function ExpandableText({
  lines,
  children,
  className,
  state,
  subject,
}: ExpandableTextProps) {
  const own = useExpandableText();
  const current = state ?? own;
  const { bodyRef, measure, expanded, bodyId } = current;

  /* Measured after every render, because the text is whatever the caller rendered this time;
     a measurement that finds the same answer sets no state, so it settles at once. */
  useLayoutEffect(() => {
    measure();
  });
  /* And again whenever the box changes width, which re-wraps the same text. */
  useLayoutEffect(() => {
    const element = bodyRef.current;
    if (element === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, bodyRef]);

  return (
    <>
      <div
        ref={bodyRef}
        id={bodyId}
        data-expanded={expanded ? 'true' : 'false'}
        data-expandable-text=""
        className={cx(!expanded && CLAMP[lines], className)}
      >
        {children}
      </div>
      {state === undefined && <ExpandableTextToggle state={current} subject={subject} />}
    </>
  );
}

/** "More" / "Less", for a block whose state it is given. Renders nothing when nothing is cut. */
export function ExpandableTextToggle({
  state,
  subject,
  className,
}: {
  readonly state: ExpandableToggleState;
  /**
   * WHAT IT OPENS, IN ITS ACCESSIBLE NAME: "More, Identity" (BR-PARTS). The label a person sees
   * stays the register's "More" and "Less" (L17, R-W1); the name a screen reader and a test read
   * starts with the same word (WCAG 2.5.3, label in name) and says what it opens, so a page with
   * a "More" menu of its own, or two expanders, has controls that can be told apart. Absent
   * keeps the bare word.
   */
  readonly subject?: string | undefined;
  readonly className?: string | undefined;
}) {
  if (!state.cut) return null;
  const word = state.expanded ? 'Less' : 'More';
  return (
    <Button
      variant="text"
      aria-expanded={state.expanded}
      aria-controls={state.bodyId}
      {...(subject === undefined ? {} : { 'aria-label': `${word}, ${subject}` })}
      onClick={() => state.setExpanded(!state.expanded)}
      className={className}
      data-expandable-toggle=""
    >
      {word}
    </Button>
  );
}
